import React, { useEffect, useState, useCallback } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import ThFilter from '../../components/ui/ThFilter';
import PropostaForm, { OPCOES_CATEGORIA } from './PropostaForm';
import PropostaDetail from './PropostaDetail';
import PedidoDetail, { STATUS_TOM, STATUS_LABEL } from './PedidoDetail';
import { propostasApi } from '../../api/vendas';
import { usuariosApi } from '../../api/admin';
import { COLORS } from '../../theme';
import { useAuth } from '../../context/AuthContext';

const CATEGORIA_LABEL = Object.fromEntries(OPCOES_CATEGORIA.filter(o => o.value).map(o => [o.value, o.label]));

const OPCOES_STATUS_EXIBICAO = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'em_negociacao', label: 'Em Negociação' },
  { value: 'enviado_almoxarifado', label: STATUS_LABEL.enviado_almoxarifado },
  { value: 'em_separacao', label: STATUS_LABEL.em_separacao },
  { value: 'aguardando_faturamento', label: STATUS_LABEL.aguardando_faturamento },
  { value: 'faturado', label: STATUS_LABEL.faturado },
  { value: 'devolvido', label: STATUS_LABEL.devolvido },
  { value: 'cancelada', label: 'Cancelada' }
];
const STATUS_EXIBICAO_TOM = { aberta: 'info', em_negociacao: 'aviso', ...STATUS_TOM, cancelada: 'perigo' };
const STATUS_EXIBICAO_LABEL = Object.fromEntries(OPCOES_STATUS_EXIBICAO.map(o => [o.value, o.label]));

const FILTROS_VAZIO = {
  numero: '', data: {}, categoria: '', vendedor_id: '', status_exibicao: '',
  pedido_numero: '', cliente_busca: '', valor: {}, numero_nota_fiscal: '', empresa_id: ''
};

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function PropostasList() {
  const { empresas } = useAuth();
  const [propostas, setPropostas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_VAZIO);
  const [registroAberto, setRegistroAberto] = useState(undefined);
  const [pedidoAberto, setPedidoAberto] = useState(null);

  const carregar = useCallback(() => {
    const params = {};
    if (filtros.numero) params.numero = filtros.numero;
    if (filtros.data.de) params.data_de = filtros.data.de;
    if (filtros.data.ate) params.data_ate = filtros.data.ate;
    if (filtros.categoria) params.categoria = filtros.categoria;
    if (filtros.vendedor_id) params.vendedor_id = filtros.vendedor_id;
    if (filtros.status_exibicao) params.status_exibicao = filtros.status_exibicao;
    if (filtros.pedido_numero) params.pedido_numero = filtros.pedido_numero;
    if (filtros.cliente_busca) params.cliente_busca = filtros.cliente_busca;
    if (filtros.valor.de) params.valor_min = filtros.valor.de;
    if (filtros.valor.ate) params.valor_max = filtros.valor.ate;
    if (filtros.numero_nota_fiscal) params.numero_nota_fiscal = filtros.numero_nota_fiscal;
    if (filtros.empresa_id) params.empresa_id = filtros.empresa_id;
    propostasApi.listar(params).then(setPropostas).catch(() => {});
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { usuariosApi.listarVendedores().then(setVendedores).catch(() => {}); }, []);

  const setFiltro = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }));
  const opcoesVendedores = vendedores.map(v => ({ value: String(v.id), label: v.nome }));
  const opcoesEmpresas = empresas.map(e => ({ value: String(e.id), label: e.nome }));

  const algumFiltroAtivo = Object.entries(filtros).some(([, v]) => (typeof v === 'object' ? (v.de || v.ate) : v));

  if (pedidoAberto) {
    return <PedidoDetail pedidoId={pedidoAberto} onVoltar={() => { setPedidoAberto(null); carregar(); }} />;
  }

  if (registroAberto === null) {
    return <PropostaForm onVoltar={() => setRegistroAberto(undefined)} onSalvo={carregar} />;
  }

  if (registroAberto !== undefined) {
    return (
      <PropostaDetail
        propostaId={registroAberto}
        onVoltar={() => { setRegistroAberto(undefined); carregar(); }}
        onAbrirPedido={setPedidoAberto}
      />
    );
  }

  return (
    <div>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 14, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          {algumFiltroAtivo
            ? <Btn variante="ghost" onClick={() => setFiltros(FILTROS_VAZIO)}>Limpar Filtros</Btn>
            : <span style={{ fontSize: 12, color: COLORS.textLight }}>Clique no título de qualquer coluna pra filtrar</span>}
          <Btn onClick={() => setRegistroAberto(null)}>+ Nova Proposta</Btn>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <ThFilter label="N° Proposta" tipo="numero" value={filtros.numero} onChange={v => setFiltro('numero', v)} />
                <ThFilter label="Empresa" tipo="select" options={opcoesEmpresas} value={filtros.empresa_id} onChange={v => setFiltro('empresa_id', v)} />
                <ThFilter label="Data" tipo="data" value={filtros.data} onChange={v => setFiltro('data', v)} />
                <ThFilter label="Categoria" tipo="select" options={OPCOES_CATEGORIA.filter(o => o.value)} value={filtros.categoria} onChange={v => setFiltro('categoria', v)} />
                <ThFilter label="Vendedor" tipo="select" options={opcoesVendedores} value={filtros.vendedor_id} onChange={v => setFiltro('vendedor_id', v)} />
                <ThFilter label="Status" tipo="select" options={OPCOES_STATUS_EXIBICAO} value={filtros.status_exibicao} onChange={v => setFiltro('status_exibicao', v)} />
                <ThFilter label="N° Pedido" tipo="numero" value={filtros.pedido_numero} onChange={v => setFiltro('pedido_numero', v)} />
                <ThFilter label="Cliente" tipo="texto" value={filtros.cliente_busca} onChange={v => setFiltro('cliente_busca', v)} />
                <ThFilter label="Valor dos Produtos" tipo="valor" value={filtros.valor} onChange={v => setFiltro('valor', v)} align="right" />
                <ThFilter label="N° da Nota Fiscal" tipo="texto" value={filtros.numero_nota_fiscal} onChange={v => setFiltro('numero_nota_fiscal', v)} />
              </tr>
            </thead>
            <tbody>
              {propostas.map(pr => (
                <tr key={pr.id} onClick={() => setRegistroAberto(pr.id)}
                  style={{ borderTop: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
                  <td style={{ padding: 10 }}>#{pr.numero}</td>
                  <td style={{ padding: 10 }}>{pr.empresa_nome || '-'}</td>
                  <td style={{ padding: 10 }}>{new Date(pr.criado_em).toLocaleDateString('pt-BR')}</td>
                  <td style={{ padding: 10 }}>{CATEGORIA_LABEL[pr.categoria] || '-'}</td>
                  <td style={{ padding: 10 }}>{pr.vendedor_nome}</td>
                  <td style={{ padding: 10 }}><Badge tom={STATUS_EXIBICAO_TOM[pr.status_exibicao]}>{STATUS_EXIBICAO_LABEL[pr.status_exibicao]}</Badge></td>
                  <td style={{ padding: 10 }}>{pr.pedido_numero ? `#${pr.pedido_numero}` : '-'}</td>
                  <td style={{ padding: 10 }}>{pr.cliente_nome}</td>
                  <td style={{ padding: 10, textAlign: 'right' }}>{formatarReais(pr.valor_total)}</td>
                  <td style={{ padding: 10 }}>{pr.numero_nota_fiscal || '-'}</td>
                </tr>
              ))}
              {propostas.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhuma proposta encontrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
